import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FaInstagram,
  FaTiktok,
  FaTwitter,
  FaLinkedinIn,
  FaEnvelope,
} from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

// ── Keyframes
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(168,85,247,0); }
  50%       { box-shadow: 0 0 0 6px rgba(168,85,247,0.12); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ── Section
const Section = styled.section`
  position: relative;
  background: #040008;
  overflow: hidden;
  padding: 130px 0 150px;
  border-top: 1px solid rgba(168, 85, 247, 0.08);
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: ${({ op }) => op || 0.15};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 clamp(1.5rem, 5vw, 4rem);
`;

// ── Two-column grid
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(3rem, 6vw, 8rem);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3.5rem;
  }
`;

// ── LEFT COLUMN ───────────────────────────────────────────────────────────────

const LeftCol = styled.div`
  opacity: 0;
  transform: translateX(-30px);
`;

const Eyebrow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1.2rem;
`;

const Title = styled.h2`
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(2.4rem, 4.5vw, 3.8rem);
  font-weight: 300;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1.4rem;

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }
`;

const BodyText = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.92rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  line-height: 1.85;
  margin-bottom: 2.5rem;
`;

// Email direct link
const EmailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
  text-decoration: none;
  margin-bottom: 2.5rem;
  transition: color 0.2s ease;

  svg {
    color: #a855f7;
    font-size: 1rem;
    flex-shrink: 0;
  }

  &:hover {
    color: #fff;
  }
`;

// Divider
const Divider = styled.div`
  width: 40px;
  height: 1px;
  background: rgba(168, 85, 247, 0.3);
  margin-bottom: 2rem;
`;

const SocialsLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
  margin-bottom: 1.2rem;
`;

const SocialsRow = styled.div`
  display: flex;
  gap: 0.9rem;
  flex-wrap: wrap;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.45);
  font-size: 1rem;
  text-decoration: none;
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    transform 0.25s ease;
  animation: ${pulse} 3s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || "0s"};
  animation-play-state: paused;

  &:hover {
    background: rgba(168, 85, 247, 0.12);
    border-color: rgba(168, 85, 247, 0.4);
    color: #c084fc;
    transform: translateY(-3px);
    animation-play-state: running;
  }
`;

// ── RIGHT COLUMN ──────────────────────────────────────────────────────────────

const RightCol = styled.div`
  opacity: 0;
  transform: translateX(30px);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.1rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const FieldLabel = styled.label`
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
`;

const inputStyles = `
  width: 100%;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 0.85rem 1.1rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.9rem;
  font-weight: 300;
  color: rgba(255,255,255,0.85);
  outline: none;
  transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
  -webkit-appearance: none;

  &::placeholder {
    color: rgba(255,255,255,0.18);
  }

  &:focus {
    border-color: rgba(168,85,247,0.5);
    background: rgba(168,85,247,0.04);
    box-shadow: 0 0 0 3px rgba(168,85,247,0.08);
  }
`;

const Input = styled.input`
  ${inputStyles}
`;

const Select = styled.select`
  ${inputStyles}
  cursor: pointer;

  option {
    background: #0d0020;
    color: rgba(255, 255, 255, 0.85);
  }
`;

const Textarea = styled.textarea`
  ${inputStyles}
  resize: none;
  height: 130px;
  line-height: 1.7;
`;

// Submit button
const SubmitBtn = styled.button`
  position: relative;
  width: 100%;
  height: 52px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #a855f7, #c026d3);
  background-size: 200% auto;
  transition:
    background-position 0.4s ease,
    transform 0.2s ease,
    box-shadow 0.3s ease;
  overflow: hidden;
  margin-top: 0.4rem;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 12%;
    right: 12%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    border-radius: 50%;
  }

  &:hover {
    background-position: right center;
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(124, 58, 237, 0.45);
  }

  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Spinner inside button
const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
`;

// Success state
const SuccessMsg = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0 auto 1.2rem;
`;

const SuccessTitle = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.8rem;
  font-weight: 300;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const SuccessBody = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.7;
`;

// Privacy note
const PrivacyNote = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.7rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.2);
  text-align: center;
  margin-top: 0.6rem;
  line-height: 1.6;
`;

// ── Socials data
const SOCIALS = [
  {
    icon: <FaInstagram />,
    href: "https://instagram.com",
    label: "Instagram",
    delay: "0s",
  },
  {
    icon: <FaTiktok />,
    href: "https://tiktok.com",
    label: "TikTok",
    delay: "0.2s",
  },
  {
    icon: <FaTwitter />,
    href: "https://twitter.com",
    label: "Twitter",
    delay: "0.4s",
  },
  {
    icon: <FaLinkedinIn />,
    href: "https://linkedin.com",
    label: "LinkedIn",
    delay: "0.6s",
  },
];

// ── Component
export default function ContactSection() {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "general",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 78%",
            end: "top 42%",
            scrub: 0.7,
          },
        })
        .to(leftRef.current, { opacity: 1, x: 0, ease: "power2.out" }, 0)
        .to(rightRef.current, { opacity: 1, x: 0, ease: "power2.out" }, 0.1);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate network — wire up your endpoint here
    await new Promise((r) => setTimeout(r, 1400));
    setLoading(false);
    setSuccess(true);
  };

  return (
    <Section ref={sectionRef} id="contact">
      <Grain />
      <Orb s="500px" c="rgba(124,58,237,0.18)" top="-5%" left="-8%" op={0.2} />
      <Orb
        s="380px"
        c="rgba(232,121,249,0.12)"
        bottom="0%"
        right="-5%"
        op={0.15}
      />

      <Container>
        <Grid>
          {/* ── LEFT */}
          <LeftCol ref={leftRef}>
            <Eyebrow>Get in touch</Eyebrow>
            <Title>
              Let's <em>talk.</em>
            </Title>
            <BodyText>
              Whether you have a question, a partnership idea, press enquiry, or
              just want to say hello — we're a real team and we read every
              message.
            </BodyText>

            <EmailLink href="mailto:hello@yourapp.com">
              <FaEnvelope />
              info@nudr.app
            </EmailLink>

            <Divider />
            <SocialsLabel>Find us on</SocialsLabel>
            <SocialsRow>
              {SOCIALS.map((s) => (
                <SocialLink
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  delay={s.delay}
                  title={s.label}
                >
                  {s.icon}
                </SocialLink>
              ))}
            </SocialsRow>
          </LeftCol>

          {/* ── RIGHT */}
          <RightCol ref={rightRef}>
            {success ? (
              <SuccessMsg>
                <SuccessIcon>✓</SuccessIcon>
                <SuccessTitle>Message sent.</SuccessTitle>
                <SuccessBody>
                  We'll get back to you within 24 hours.
                  <br />
                  Talk soon.
                </SuccessBody>
              </SuccessMsg>
            ) : (
              <Form onSubmit={handleSubmit}>
                <FieldRow>
                  <FieldWrap>
                    <FieldLabel htmlFor="name">Your name</FieldLabel>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel htmlFor="email">Email address</FieldLabel>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jane@email.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </FieldWrap>
                </FieldRow>

                <FieldWrap>
                  <FieldLabel htmlFor="subject">Subject</FieldLabel>
                  <Select
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                  >
                    <option value="general">General enquiry</option>
                    <option value="press">Press & media</option>
                    <option value="partnership">Partnership</option>
                    <option value="support">Support</option>
                    <option value="security">Security report</option>
                    <option value="other">Other</option>
                  </Select>
                </FieldWrap>

                <FieldWrap>
                  <FieldLabel htmlFor="message">Message</FieldLabel>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us what's on your mind..."
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </FieldWrap>

                <SubmitBtn type="submit" disabled={loading}>
                  {loading && <Spinner />}
                  {loading ? "Sending..." : "Send message"}
                </SubmitBtn>

                <PrivacyNote>
                  We'll never share your details with anyone. Ever.
                </PrivacyNote>
              </Form>
            )}
          </RightCol>
        </Grid>
      </Container>
    </Section>
  );
}
